import React, {useState} from "react";
import {Box, Button, Grid, Paper, TextField, Tooltip} from "@material-ui/core"
import useDataFetchers from "../../../../../hooks/useDataFetchers";
import SingleAsyncAutoComplete from "../../../shared/SingleAsyncAutoComplete";
import {emergingManagers} from "../../InvestorConstants";
import InputComboBox from "../../../sharedFormComponents/InputComboBox";
import FilterAutoComplete from "../../../shared/FilterAutoComplete";
import AdminClient from "../../../client/AdminClient";
import AddIcon from '@material-ui/icons/Add';
import CreateTextDialog from "../../../shared/Dialogs/CreateTextDialog";

const booleanOptions = [
    {id: "true", name: "Yes"},
    {id: "false", name: "No"}
]

const getSelectedOptions = (selected, options) => {
    return selected?.map((selectedId) => options.find(({id}) => parseInt(id) === parseInt(selectedId)) || null).filter((option) => option !== null) || []
}

const filteredGeographicFocuses = [
    "Africa",
    "Asia",
    "Europe",
    "Global",
    "Latin America",
    "Middle East",
    "North America",
    "Oceania",
]

const SummarySection = (
    {
        investor, isEditing, setInvestor,
        handleOnChange, handleOnChangeInField, handleShowSnackBar,
        session, industryWealthOrigins,
        assetClassFocuses, geographicFocuses, ...props
    }
) => {
    const {fetchData: resetIndustryWealthOrigins, data: industryWealthOriginsOptions} = industryWealthOrigins;
    const [selectedIndustryWealthOrigins, setSelectedIndustryWealthOrigins] = useState(getSelectedOptions(investor.industry_wealth_origin_ids, industryWealthOriginsOptions));
    const [selectedAssetClassFocuses, setSelectedAssetClassFocuses] = useState(getSelectedOptions(investor.invest_in_ids, assetClassFocuses));
    const [selectedGeographicFocuses, setSelectedGeographicFocuses] = useState(getSelectedOptions(investor.geographic_area_ids, geographicFocuses));
    const [investorSummary, setInvestorSummary] = useState(investor.investor_summary);
    const [allocationPreferences, setAllocationPreferences] = useState(investor.allocation_preferences);
    const [openFamilyWealthOrigin, setOpenFamilyWealthOrigin] = useState(false);
    const [openIndustryWealthOrigin, setOpenIndustryWealthOrigin] = useState(false);

    const fetchers = useDataFetchers(session);

    const handleFamilyWealthOriginChange = (familyWealthOrigin) => {
        handleOnChangeInField('family_wealth_origin_id', familyWealthOrigin?.id);
        handleOnChangeInField('family_wealth_origin_name', familyWealthOrigin?.name);
    }

    const handleAssetClassFocusChange = (assetClassFocuses) => {
        setSelectedAssetClassFocuses(assetClassFocuses)
        handleOnChangeInField('invest_in_ids', assetClassFocuses.map(({id}) => id))
    }

    const handleIndustryWealthOriginsChange = (industryWealthOrigins) => {
        setSelectedIndustryWealthOrigins(industryWealthOrigins)
        handleOnChangeInField('industry_wealth_origin_ids', industryWealthOrigins.map(({id}) => id))
    }

    const handleGeographicFocusesChange = (geographicFocuses) => {
        setSelectedGeographicFocuses(geographicFocuses)
        handleOnChangeInField('geographic_area_ids', geographicFocuses.map(({id}) => id))
    }

    const handleOpenFamilyWealthOrigin = () => setOpenFamilyWealthOrigin(true);
    const handleCloseFamilyWealthOrigin = () => setOpenFamilyWealthOrigin(false);
    const handleOpenIndustryWealthOrigin = () => setOpenIndustryWealthOrigin(true);
    const handleCloseIndustryWealthOrigin = () => setOpenIndustryWealthOrigin(false);

    const handleCreateFamilyWealthOrigin = async (familyWealthOrigin) => {
        try {
            const createResponse = await AdminClient.post(`/v1/admin/family_wealth_origins/`, session, {family_wealth_origin: {name: familyWealthOrigin}});
            if (createResponse.data.status === 'success') {
                handleFamilyWealthOriginChange(createResponse.data.family_wealth_origin)
                handleShowSnackBar("Family Wealth Origin created successfully", "success");
            } else {
                handleShowSnackBar(createResponse.data.message, "warning");
            }
        } catch (e) {
            console.error(e);
            handleShowSnackBar("Error when creating Family Wealth Origin", "warning");
        } finally {
            handleCloseFamilyWealthOrigin();
        }
    }

    const handleCreateIndustryWealthOrigin = async (industryWealthOrigin) => {
        try {
            const createResponse = await AdminClient.post(`/v1/admin/industry_wealth_origins/`, session, {industry_wealth_origin: {name: industryWealthOrigin}});
            if (createResponse.data.status === 'success') {
                resetIndustryWealthOrigins();
                handleIndustryWealthOriginsChange(
                    [
                        ...selectedIndustryWealthOrigins,
                        {id: createResponse.data.industry_wealth_origin.id, name: createResponse.data.industry_wealth_origin.name}
                    ]
                )
                handleShowSnackBar("Industry Wealth Origin created successfully", "success");
            } else {
                handleShowSnackBar(createResponse.data.message, "warning");
            }
        } catch (e) {
            console.error(e);
            handleShowSnackBar("Error when creating Industry Wealth Origin", "warning");
        } finally {
            handleCloseIndustryWealthOrigin();
        }
    }

    return (
        <>
            <Grid container item xs={12}>
                <Paper className="form-container" elevation={3}>
                    <Grid item xs={12}>
                        <h3>Summary</h3>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item container xs={3}>
                            <Grid item xs={9}>
                                <SingleAsyncAutoComplete
                                    fetch={fetchers.familyWealthOrigins}
                                    handleOnChange={(value) => handleFamilyWealthOriginChange(value)}
                                    value={{id: investor.family_wealth_origin_id, name: investor.family_wealth_origin_name}}
                                    field={'family_wealth_origin_id'}
                                    label={"Family Wealth Origin"}
                                />
                            </Grid>

                            <Grid item xs={3} style={{display: "flex", padding: "8px"}}>
                                <Tooltip title="New Family Wealth Origin">
                                    <Button onClick={handleOpenFamilyWealthOrigin} className="create" style={{minWidth: "unset"}}>
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>

                        <Grid item xs={3}>
                            <InputComboBox
                                value={investor.emerging_manager}
                                label={'Emerging Manager Allocator'}
                                field={'emerging_manager'}
                                name={'emerging_manager'}
                                handleOnChange={({target:{ name, value }}) => handleOnChangeInField(name, value)}
                                options={emergingManagers}
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <InputComboBox
                                value={investor.emerging_markets}
                                label={'Emerging Markets'}
                                field={'emerging_markets'}
                                name={'emerging_markets'}
                                handleOnChange={({target:{ name, value }}) => handleOnChangeInField(name, value)}
                                options={booleanOptions}
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <InputComboBox
                                value={investor.co_invest}
                                label={'Co-Investment Activity'}
                                field={'co_invest'}
                                name={'co_invest'}
                                handleOnChange={({target:{ name, value }}) => handleOnChangeInField(name, value)}
                                options={booleanOptions}
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <InputComboBox
                                value={investor.impact_investor}
                                label={'ESG Investor'}
                                field={'impact_investor'}
                                name={'impact_investor'}
                                handleOnChange={({target:{ name, value }}) => handleOnChangeInField(name, value)}
                                options={booleanOptions}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FilterAutoComplete
                                options={industryWealthOriginsOptions}
                                handleOnChange={(value) => handleIndustryWealthOriginsChange(value)}
                                selected={selectedIndustryWealthOrigins}
                                label={'Industry Wealth Origin'}
                                field={'industry_wealth_origin_ids'}
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FilterAutoComplete
                                options={assetClassFocuses}
                                handleOnChange={(value) => handleAssetClassFocusChange(value)}
                                selected={selectedAssetClassFocuses}
                                label={'Asset Class Focus'}
                                field={'invest_in_ids'}
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FilterAutoComplete
                                options={geographicFocuses.filter(({name}) => filteredGeographicFocuses.includes(name))}
                                handleOnChange={(value) => handleGeographicFocusesChange(value)}
                                selected={selectedGeographicFocuses}
                                label={'Geographic Focus'}
                                field={'geographic_area_ids'}
                                multiple
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Investor Summary"
                                    variant="outlined"
                                    name={'investor_summary'}
                                    onChange={(event) => setInvestorSummary(event.target.value)}
                                    onBlur={handleOnChange}
                                    value={investorSummary}
                                    multiline={true}
                                    maxRows={14}
                                    minRows={14}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Allocation Preferences"
                                    variant="outlined"
                                    name={'allocation_preferences'}
                                    onChange={(event) => setAllocationPreferences(event.target.value)}
                                    onBlur={handleOnChange}
                                    value={allocationPreferences}
                                    multiline={true}
                                    maxRows={14}
                                    minRows={14}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <CreateTextDialog
                open={openFamilyWealthOrigin}
                body={""}
                label={"Family Wealth Origin"}
                handleClose={handleCloseFamilyWealthOrigin}
                handleSave={handleCreateFamilyWealthOrigin}
            />

            <CreateTextDialog
                open={openIndustryWealthOrigin}
                body={""}
                label={"Industry Wealth Origin"}
                handleClose={handleCloseIndustryWealthOrigin}
                handleSave={handleCreateIndustryWealthOrigin}
            />
        </>
    )
}

export default SummarySection;